import './Header.css';
import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Search } from 'lucide-react';
import SearchModal from '../../ui/search/SearchModal';
import { getCategories, getFooterSettings } from '../../../services/settingsCache';

const defaultNavLinks = [
  { label: 'HOME', to: '/' },
  { label: 'PRODUCTS', to: '/products' },
  { label: 'SERVICES', to: '/services' },
  { label: 'ABOUT', to: '/about' },
  { label: 'CONTACT', to: '/contact' },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [branding, setBranding] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadHeaderData = async () => {
      try {
        const [categoryData, footerSettings] = await Promise.all([
          getCategories(),
          getFooterSettings(),
        ]);

        if (cancelled) return;

        setCategories(Array.isArray(categoryData) ? categoryData : []);
        setBranding(footerSettings || null);
      } catch (err) {
        console.error('Failed to load header data', err);
      }
    };

    loadHeaderData();

    return () => {
      cancelled = true;
    };
  }, []);

  const companyName = branding?.companyName || 'Tiwari Syndicate';
  const companyTagline =
    branding?.companyTagline || ' TRUST. WE BUILD SMILES.';
  const logoUrl = branding?.companyLogoUrl || `${process.env.PUBLIC_URL}/Group.svg`;

  const mainCategories = categories.filter(cat => !cat.parentCategory);
  const productLinks = mainCategories.map((item) => ({
    label: item.name,
    to: `/products/${item.slug}`,
  }));

  return (
    <header className={`site-header ${open ? 'nav-open' : ''}`}>
      <nav className="primary-nav" aria-label="Main">
        <div className="nav-brand">
          <NavLink
            className="nav-logo"
            to="/"
            aria-label={`${companyName} home`}
            onClick={() => setOpen(false)}
          >
            <img src={logoUrl} alt={companyName} width="44" height="44" decoding="async" />
          </NavLink>
          <div className="nav-brand__copy">
            <span className="nav-brand__title">{companyName}</span>
            <span className="nav-brand__tagline">{companyTagline}</span>
          </div>
        </div>
        <button
          type="button"
          className="nav-toggle"
          aria-label="Toggle menu"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M3 6h18M3 12h18M3 18h18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <ul className="nav-links">
          {defaultNavLinks.map((link) => (
            <li key={link.label}>
              <NavLink
                to={link.to}
                className={({ isActive }) => (isActive ? 'is-current' : undefined)}
                onClick={() => setOpen(false)}
              >
                {link.label}
              </NavLink>
            </li>
          ))}
        </ul>
        <div className="nav-actions" aria-label="Header actions">
          <button
            type="button"
            className="icon-button icon-button--search"
            aria-label="Search"
            onClick={() => setSearchOpen(true)}
          >
            <Search className="icon-image" aria-hidden="true" />
          </button>
          {/* Temporary hidden login button
          <NavLink to="/login" className="nav-login-btn" style={{ marginLeft: '10px' }}>
            LOGIN
          </NavLink>
          */}
        </div>
        <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
      </nav>

      <div className="mobile-drawer" aria-hidden={!open}>
        <div className="mobile-drawer__header">
          <div className="mobile-drawer__brand">
            <img src={logoUrl} alt={companyName} width="44" height="44" decoding="async" />
            <span>{companyName}</span>
          </div>
          <button
            type="button"
            className="mobile-drawer__close"
            aria-label="Close menu"
            onClick={() => setOpen(false)}
          >
            x
          </button>
        </div>

        <nav className="mobile-drawer__nav" aria-label="Mobile primary">
          <NavLink
            to="/"
            className={({ isActive }) => (isActive ? 'is-current' : undefined)}
            onClick={() => setOpen(false)}
          >
            HOME
          </NavLink>

          {/* Temporary hidden mobile login link
          <div className="mobile-drawer__section-label">ACCOUNT</div>
          <NavLink to="/login" onClick={() => setOpen(false)}>
            Login / Sign Up
          </NavLink>
          */}

          <div className="mobile-drawer__section-label">PRODUCTS</div>
          <NavLink to="/products" onClick={() => setOpen(false)}>
            All Products
          </NavLink>
          {productLinks.map((link) => (
            <NavLink key={link.to} to={link.to} onClick={() => setOpen(false)}>
              {link.label}
            </NavLink>
          ))}
          {productLinks.length === 0 && (
            <span style={{ fontSize: 12, color: '#94a3b8' }}>Loading categories...</span>
          )}

          <div className="mobile-drawer__section-label">SERVICES</div>
          <NavLink to="/services" onClick={() => setOpen(false)}>
            Services
          </NavLink>

          <div className="mobile-drawer__section-label">ABOUT</div>
          <NavLink to="/about" onClick={() => setOpen(false)}>
            About
          </NavLink>

          <div className="mobile-drawer__section-label">CONTACT</div>
          <NavLink to="/contact" onClick={() => setOpen(false)}>
            Contact
          </NavLink>
        </nav>

        <div className="mobile-drawer__footer">
          <button
            type="button"
            className="mobile-drawer__action"
            onClick={() => {
              setOpen(false);
              setSearchOpen(true);
            }}
          >
            <Search className="mobile-drawer__action-icon" aria-hidden="true" />
            <span>Search</span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
